import React, { useState } from 'react';
import { Element } from '../types';
import { elementsAPI } from '../services/api';
import './CreateElementModal.css';

interface CreateElementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onElementCreated: (element: Element) => void;
}

const CreateElementModal: React.FC<CreateElementModalProps> = ({
  isOpen,
  onClose,
  onElementCreated
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    rarity: 'common' // default to lowercase
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setProgress(0);
      setProgressMessage('Preparing to create element...');

      // Step 1: Validate form data
      setProgress(15);
      setProgressMessage('Validating form data...');
      await new Promise(resolve => setTimeout(resolve, 200));

      // Step 2: Prepare data
      setProgress(30);
      setProgressMessage('Preparing element data...');
      await new Promise(resolve => setTimeout(resolve, 300));

      // Step 3: Create on server
      setProgress(60);
      setProgressMessage('Creating element on server...');
      const newElement = await elementsAPI.create({
        name: formData.name.trim(),
        description: formData.description.trim(),
        category: formData.category.trim(),
        rarity: formData.rarity // already lowercase
      });

      // Step 4: Finalize
      setProgress(90);
      setProgressMessage('Finalizing creation...');
      await new Promise(resolve => setTimeout(resolve, 200));
      
      setProgress(100);
      setProgressMessage('Element created successfully!');
      await new Promise(resolve => setTimeout(resolve, 500));

      // Show toast notification on success
      if (typeof window.showToast === 'function') {
        window.showToast('Element created successfully!', 'success');
      }

      onElementCreated(newElement);
      
      // Reset form
      setFormData({
        name: '',
        description: '',
        category: '',
        rarity: 'common' // reset to lowercase
      });
      
      onClose();
    } catch (error) {
      console.error('Failed to create element:', error);
      setError('Failed to create element. Please try again.');
      setProgressMessage('Failed to create element');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: '',
      description: '',
      category: '',
      rarity: 'common' // reset to lowercase
    });
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Create New Element</h2>
          <button className="modal-close" onClick={handleCancel}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {loading && (
            <div className="progress-container">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <div className="progress-message">{progressMessage}</div>
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="name">Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter element name"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter element description"
              rows={4}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="category">Category</label>
            <input
              type="text"
              id="category"
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              placeholder="Enter category"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="rarity">Rarity</label>
            <select
              id="rarity"
              name="rarity"
              value={formData.rarity}
              onChange={handleInputChange}
              disabled={loading}
            >
              <option value="common">Common</option>
              <option value="uncommon">Uncommon</option>
              <option value="rare">Rare</option>
              <option value="legendary">Legendary</option>
              <option value="mythic">Mythic</option>
            </select>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="modal-actions">
            <button
              type="button"
              className="btn btn--gray"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn--green"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Element'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateElementModal; 